// src/pages/PrivacyPolicy.tsx  (/privacy-policy)
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Link } from "@mui/material";
import LegalPageLayout, { LegalSection, LegalText, LegalList } from "../components/LegalPageLayout";
import { LEGAL } from "../legal/legalConfig";

const PrivacyPolicy: React.FC = () => (
    <LegalPageLayout title="Adatvédelmi nyilatkozat" updated={LEGAL.effectiveDate}>
        <LegalText>
            Jelen nyilatkozat a {LEGAL.brandName} weboldalon ({LEGAL.website}) és a később megjelenő Chillag mobilalkalmazásban
            végzett személyes adatkezelésről tájékoztatja a felhasználókat az (EU) 2016/679 rendelet (GDPR) és az információs
            önrendelkezési jogról és az információszabadságról szóló 2011. évi CXII. törvény alapján.
        </LegalText>

        <LegalSection title="1. Az adatkezelő adatai">
            <LegalList
                items={[
                    <>Név: {LEGAL.companyName}</>,
                    <>Székhely: {LEGAL.address}</>,
                    <>Adószám: {LEGAL.taxNumber}</>,
                    <>E-mail: {LEGAL.email}</>,
                    <>Telefon: {LEGAL.phone}</>,
                ]}
            />
        </LegalSection>

        <LegalSection title="2. A kezelt adatok köre, célja és jogalapja">
            <LegalList
                items={[
                    <>
                        <strong>Regisztráció és bejelentkezés</strong>: név, e-mail cím, jelszó (a jelszót nem mi tároljuk, hanem
                        a hitelesítési szolgáltatónk, a Clerk), profilkép. A weboldal saját adatbázisában a név, az e-mail cím,
                        a Clerk-azonosító és a szerepkör (jogosultság) található, kizárólag azért, hogy a Clerk, a GHL és az oldal
                        szerepkör-funkciói szinkronban legyenek. Cél: a felhasználói fiók létrehozása és használata. Jogalap:
                        szerződés teljesítése (GDPR 6. cikk (1) b)).
                    </>,
                    <>
                        <strong>Hírlevél</strong>: név és e-mail cím. Cél: a {LEGAL.brandName}hoz kötődő hírlevelek küldése.
                        Jogalap: az érintett hozzájárulása (GDPR 6. cikk (1) a)). A hozzájárulás bármikor visszavonható, a hírlevélről
                        bármikor leiratkozhat (a hírlevelekben található leiratkozó hivatkozással vagy a {LEGAL.email} címen).
                    </>,
                    <>
                        <strong>Kapcsolatfelvétel</strong>: név, e-mail cím, az üzenet tartalma. Cél: a megkeresések megválaszolása.
                        Jogalap: az érintett kérése, illetve jogos érdek (GDPR 6. cikk (1) b) és f)).
                    </>,
                    <>
                        <strong>Gyermekekkel kapcsolatos adatok</strong>: gyermekek nevét, életkorát vagy egyéb személyes adatait
                        nem tároljuk.
                    </>,
                    <>
                        <strong>Technikai adatok és sütik</strong>: a bejelentkezéshez és a munkamenethez szükséges sütiket kizárólag a
                        Clerk használja; emellett naplóadatok (IP-cím, böngésző) keletkezhetnek. Jogalap: jogos érdek a weboldal
                        biztonságos működtetésére. Analitikai vagy marketing sütiket nem használunk.
                    </>,
                ]}
            />
            <LegalText>Fizetés és számlázás nincs az oldalon, ezért ilyen adatokat nem kezelünk.</LegalText>
        </LegalSection>

        <LegalSection title="3. Adatfeldolgozók és adattovábbítás">
            <LegalText>Az adatkezelő az alábbi adatfeldolgozókat veszi igénybe:</LegalText>
            <LegalList
                items={[
                    <>
                        <strong>Clerk, Inc.</strong>: bejelentkezés, regisztráció és felhasználói fiókok kezelése. A szolgáltató az Egyesült
                        Államokban működik, ezért az adatok harmadik országba kerülhetnek; ennek garanciáit (pl. az EU–USA adatvédelmi
                        keretrendszer vagy általános szerződési feltételek) a szolgáltató dokumentációja tartalmazza.
                    </>,
                    <>
                        <strong>GHL (GoHighLevel / HighLevel Inc.)</strong>: kapcsolattartás, ügyfélnyilvántartás és hírlevélküldés. A
                        szolgáltató az Egyesült Államokban működik, ezért az adatok harmadik országba kerülhetnek; ennek garanciáit a
                        szolgáltató dokumentációja tartalmazza.
                    </>,
                    <>
                        <strong>Tárhelyszolgáltató</strong>: {LEGAL.hostingProvider}. A weboldal és az adatbázis szerverei
                        a szolgáltató infrastruktúráján üzemelnek.
                    </>,
                    <>
                        <strong>Google (Gmail)</strong>: a kapcsolatfelvételi űrlapon küldött üzenetek e-mailben történő fogadása.
                    </>,
                ]}
            />
            <LegalText>Személyes adatokat az adatkezelő jogszabályi kötelezettség hiányában harmadik félnek nem ad ki.</LegalText>
        </LegalSection>

        <LegalSection title="4. Az adatkezelés időtartama">
            <LegalList
                items={[
                    "Az adatokat addig kezeljük, amíg a felhasználó nem kéri azok törlését.",
                    "Ha a felhasználó törli a fiókját, az adatai azonnal törlődnek a Clerk rendszeréből és a weboldal saját adatbázisából.",
                    "A GHL-ben tárolt kapcsolati adatokat (pl. hírlevél-feliratkozás) a leiratkozásig, illetve a törlési kérelem teljesítéséig kezeljük.",
                    "Kapcsolatfelvételi üzenetek: a megkeresés lezárásáig, illetve a törlési kérelemig.",
                ]}
            />
        </LegalSection>

        <LegalSection title="5. Gyermekek személyes adatai">
            <LegalText>
                Magyarországon a 16. életévét be nem töltött gyermek személyes adatainak kezeléséhez, ha az hozzájáruláson alapul,
                a szülői felügyeletet gyakorló személy hozzájárulása szükséges. Szolgáltatásainkat gyermekek számára is nyújtjuk,
                azonban gyermekek nevét, életkorát és egyéb személyes adatait nem tároljuk. A regisztrációt a szülő vagy törvényes
                képviselő végezheti, illetve engedélyezheti.
            </LegalText>
        </LegalSection>

        <LegalSection title="6. Az érintettek jogai">
            <LegalText>Ön kérelmezheti:</LegalText>
            <LegalList
                items={[
                    "tájékoztatást és hozzáférést a kezelt személyes adataihoz,",
                    "a pontatlan adatok helyesbítését,",
                    "az adatok törlését („elfeledtetéshez való jog”), ha ennek törvényi akadálya nincs,",
                    "az adatkezelés korlátozását,",
                    "az adatok hordozhatóságát,",
                    "tiltakozhat a jogos érdeken alapuló adatkezelés ellen,",
                    "a hozzájárulását bármikor visszavonhatja (a visszavonás előtti adatkezelés jogszerűségét ez nem érinti).",
                ]}
            />
            <LegalText>
                Kérelmét a {LEGAL.email} címen nyújthatja be; azt legfeljebb egy hónapon belül megválaszoljuk. A fiókja és az
                adatai nagy részét a <Link component={RouterLink} to="/profile">Saját profil</Link> oldalon is kezelheti.
            </LegalText>
        </LegalSection>

        <LegalSection title="7. Jogorvoslat">
            <LegalText>
                Ha úgy érzi, hogy személyes adatai kezelése sérti a jogszabályokat, panasszal fordulhat a Nemzeti Adatvédelmi és
                Információszabadság Hatósághoz (NAIH): 1055 Budapest, Falk Miksa utca 9-11., honlap:{" "}
                <Link href="https://naih.hu" target="_blank" rel="noopener noreferrer">naih.hu</Link>. Bírósághoz is fordulhat; a per
                a lakóhelye vagy tartózkodási helye szerinti törvényszék előtt is megindítható.
            </LegalText>
        </LegalSection>

        <LegalSection title="8. Adatbiztonság">
            <LegalText>
                Az adatkezelő megfelelő technikai és szervezési intézkedésekkel védi a személyes adatokat (titkosított kapcsolat,
                hozzáférés-korlátozás, a jelszavak biztonságos kezelése a hitelesítési szolgáltatónál).
            </LegalText>
        </LegalSection>

        <LegalSection title="9. A nyilatkozat módosítása">
            <LegalText>
                Az adatkezelő a nyilatkozatot egyoldalúan módosíthatja. A módosítás a weboldalon való közzététellel lép hatályba.
                Az ÁSZF-et a <Link component={RouterLink} to="/terms-of-service">Általános Szerződési Feltételek</Link> oldalon találja.
            </LegalText>
        </LegalSection>
    </LegalPageLayout>
);

export default PrivacyPolicy;